// "Spotlight" — a Home rail of faces, each opening onto that creator's page.
// The header and "See all" open the ranked list (Spotlights).
//
// Three bands, in this order:
//   1. Promote, always first and never moved. It is the way in for anyone who
//      wants a card of their own, so it wears the same gem as the paid cards.
//   2. Paid Promotions, gold ring and gem, ordered by lifetime value — which
//      with a single price is really longest-standing supporter first.
//   3. Everyone else, shuffled, so every creator gets the front of their band
//      sometimes rather than only the ones with the most clips.
//
// A creator who buys a Promotion is dropped from band 3: they would otherwise
// appear twice in one row, gold above and blue below.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Rocket, Spotlight } from 'lucide-react';
import Rail from './Rail';
import { Face } from './Spotlights';
import { creatorName } from '../utils/creators';
import { useTechniques } from '../hooks/useTechniques';
import { useRankedCreators } from '../hooks/useRankedCreators';
import { usePromotions } from '../hooks/usePromotions';
import { FaceSkeletons } from './RailSkeleton';
import type { LivePromotion } from '../api/promotions';

/** How many faces the rail carries; the rest are one "See all" away. */
const SHOWN = 12;

/** The front of the row — roughly what a phone shows before the first swipe.
 *  These are the cells that take turns holding a different creator. */
const ROTATE_SLOTS = 4;

/** How long a face holds the front of the row before one of them changes.
 *  One at a time, so the row breathes rather than reshuffling itself. */
const ROTATE_MS = 5000;

/** One shuffle per app load, so the row holds still while the tab is open
 *  and comes up fresh next time. */
const SEED = Math.random();
function shuffled<T>(list: T[]): T[] {
  let x = Math.floor(SEED * 2 ** 31) || 1;
  const rand = () => { x ^= x << 13; x ^= x >>> 17; x ^= x << 5; return ((x >>> 0) % 10000) / 10000; };
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export default function TopCreators({ onOpen, onSponsor, onPromote }: {
  /** Open the creators screen — on one creator, or on the list. */
  onOpen: (handle?: string) => void;
  /** Open a sponsor's page. Not the creator page: a sponsor may have no clips. */
  onSponsor: (promo: LivePromotion) => void;
  /** Open the buy form. */
  onPromote: () => void;
}) {
  const { techniques, loading } = useTechniques();
  const ranked = useRankedCreators(techniques);
  const promos = usePromotions();

  // Everyone eligible for a free slot, shuffled. The row shows SHOWN of them
  // and the rest are the stock the front cells draw from as they turn over.
  const pool = useMemo(() => {
    const promoted = new Set(promos.map((p) => p.handle));
    return shuffled(ranked.map(([handle]) => handle).filter((h) => !promoted.has(h)));
  }, [ranked, promos]);

  // Who is standing in for whom: slot index -> the handle now holding it.
  // Only the turnover is kept, and the row is worked out from the pool each
  // render. Holding the row itself in state meant reseeding it whenever the
  // pool changed, and usePromotions hands back a fresh [] on every render for
  // a guest — so the pool was new every render, the reseed ran every render,
  // and the page died on "Too many re-renders".
  const [swaps, setSwaps] = useState<Record<number, string>>({});
  const picks = useMemo(() => {
    const row = pool.slice(0, SHOWN);
    const taken = new Set(row);
    const inPool = new Set(pool);
    for (const [slot, handle] of Object.entries(swaps)) {
      const i = Number(slot);
      // Skip a stand-in who is already on the row, or who has since left the
      // catalog: a stale handle would render as a broken portrait.
      if (i >= row.length || taken.has(handle) || !inPool.has(handle)) continue;
      taken.delete(row[i]);
      row[i] = handle;
      taken.add(handle);
    }
    return row;
  }, [pool, swaps]);

  // Swiped once, the row is the reader's: it holds still from then on.
  const [swiped, setSwiped] = useState(false);
  const onScrolled = useCallback(() => setSwiped(true), []);

  // What is on the row right now, for the timer below to pick around without
  // restarting itself every time a slot changes hands.
  const picksRef = useRef(picks);
  useEffect(() => { picksRef.current = picks; }, [picks]);

  // Every few seconds one of the front cells hands its place to a creator who
  // is not on the row, so the same faces aren't the whole of Gnosis to
  // somebody who never swipes. The paid cards sit ahead of these and never
  // move — that placement is what was bought — and nothing turns over while
  // the tab is in the background or once the reader has taken hold of the row.
  useEffect(() => {
    if (swiped || pool.length <= SHOWN) return;
    let slot = 0;
    const id = setInterval(() => {
      if (document.hidden) return;
      const cur = picksRef.current;
      if (cur.length === 0) return;
      const spare = pool.filter((h) => !cur.includes(h));
      if (spare.length === 0) return;
      const taking = spare[Math.floor(Math.random() * spare.length)];
      const i = slot % Math.min(ROTATE_SLOTS, cur.length);
      slot++;
      setSwaps((prev) => ({ ...prev, [i]: taking }));
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [pool, swiped]);

  return (
    <Rail title="Spotlight" icon={<Spotlight size={16} />} onSeeAll={() => onOpen()} seeAllLabel="Top Creators" onScrolled={onScrolled}>
      <button type="button" className="rail-creator rail-promote" onClick={onPromote}>
        <span className="rail-face rail-face--slot"><Rocket size={22} /></span>
        <span className="rail-creator-name">Promote</span>
      </button>

      {promos.map((p) => (
        <button type="button" key={p.uid} className="rail-creator" onClick={() => onSponsor(p)}>
          {/* The gold ring is styled off this wrapper rather than a second
              class on Face: Face builds its no-portrait variant by appending
              "--none" to whatever className it is given, which only works on
              a single class name. */}
          <span className="rail-promo-face">
            <Face className="rail-face" handle={p.handle} />
          </span>
          <span className="rail-creator-name rail-creator-name--gold">{p.name}</span>
        </button>
      ))}

      {/* The catalog is what ranks the creators, so until it lands there is
          nobody to show: placeholder faces beside the Promote button. */}
      {loading && <FaceSkeletons />}

      {/* Keyed by the handle, so when a slot changes hands React swaps just
          that one cell and its arrival animation runs there alone. */}
      {picks.map((handle) => (
        <button type="button" key={handle} className="rail-creator rail-creator--in" onClick={() => onOpen(handle)}>
          <Face className="rail-face" handle={handle} />
          <span className="rail-creator-name">{creatorName(handle)}</span>
        </button>
      ))}
    </Rail>
  );
}
